import { useMemo, useState } from 'react';
import { FileSpreadsheet, FileText, Search } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { useToast } from '@/components/ui/useToast';
import {
  issueInvoice,
  listInvoices,
  type SalesInvoice,
} from '@/api/salesInvoice';
import { formatDate, formatINR } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { apiError } from '@/lib/apiError';
import { CreateInvoiceModal } from './CreateInvoiceModal';
import { InvoiceDetailModal } from './InvoiceDetailModal';
import { useAuth } from '@/auth/useAuth';

const TONE: Record<SalesInvoice['status'], 'accent' | 'success' | 'muted' | 'danger'> = {
  Draft: 'accent',
  Issued: 'success',
  Partial: 'accent',
  Paid: 'success',
  Cancelled: 'danger',
};

export function SalesInvoicePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const doc = useDocViewer();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [openWith, setOpenWith] = useState<'edit' | 'cancel' | undefined>();
  const canEdit = useAuth().hasPerm('acc-invoice-edit');

  // Invoice date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(
    () => ({ status: status || undefined, from: range?.from, to: range?.to }),
    [status, range],
  );

  const query = useQuery({
    queryKey: ['sales-invoices', filter],
    queryFn: () => listInvoices(filter),
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  const issueMut = useMutation({
    mutationFn: issueInvoice,
    onSuccess: (r) => {
      toast.pushStep(`Invoice ${r.invoiceNo} issued`, {
        body: 'Next step — raise the Gate Pass for outbound dispatch.',
        href: '/sales/gate-pass',
        nextLabel: 'Open Gate Pass',
        tag: `invoice-issued-${r.id}`,
      });
      qc.invalidateQueries({ queryKey: ['sales-invoices'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  return (
    <div>
      <SectionHeader
        title="Invoice"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search invoices"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Draft</option>
            <option>Issued</option>
            <option>Partial</option>
            <option>Paid</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Chip lives inside the filter block on both viewports.
            Mobile: full-width row below the fields (col-span-3) so the long
            label fits. Desktop: right-pinned via ml-auto. */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ Generate Invoice
        </button>
      </FilterBar>

      <Table<SalesInvoice>
        storageKey="sales-invoice"
        title="Invoices"
        count={`${data.length} records`}
        rows={data}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          {
            key: 'invoiceNo',
            header: 'INV #',
            cell: (r) => <span className="font-extrabold text-accent">{r.invoiceNo}</span>,
          },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.invoiceDate) },
          { key: 'do', header: 'DO', cell: (r) => r.dispatchOrder?.doNo ?? '—' },
          { key: 'grn', header: 'Outward GRN', cell: (r) => r.outwardGrn?.grnNo ?? '—' },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.party?.name ?? '—'}</span> },
          {
            key: 'gst',
            header: 'GST Type',
            cell: (r) => (
              <Badge tone={r.isInterstate ? 'accent' : 'muted'}>
                {r.isInterstate ? 'IGST' : 'CGST+SGST'}
              </Badge>
            ),
          },
          {
            key: 'taxable',
            header: 'Taxable',
            align: 'right',
            cell: (r) => formatINR(Number(r.taxableTotal)),
          },
          {
            key: 'gst-amt',
            header: 'GST',
            align: 'right',
            cell: (r) => formatINR(Number(r.totalGst)),
          },
          {
            key: 'total',
            header: 'Grand',
            align: 'right',
            cell: (r) => <strong>{formatINR(Number(r.grandTotal))}</strong>,
          },
          { key: 'due', header: 'Due', cell: (r) => formatDate(r.dueDate) },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <Badge tone={TONE[r.status]}>{r.status}</Badge>,
          },
          {
            key: 'actions',
            header: '',
            width: '420px',
            cell: (r) => (
              <div className="flex gap-1">
                {r.status === 'Draft' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => issueMut.mutate(r.id)}
                    disabled={issueMut.isPending}
                  >
                    Issue
                  </Button>
                )}
                {r.status === 'Draft' && canEdit && (
                  <Button variant="glass" size="sm" onClick={() => { setOpenWith('edit'); setOpenId(r.id); }}>
                    Edit
                  </Button>
                )}
                <Button variant="glass" size="sm" onClick={() => { setOpenWith(undefined); setOpenId(r.id); }}>
                  Open
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => doc.open('sales-invoice', r.id, r.invoiceNo)}
                  title="View / Print Invoice (PDF)"
                >
                  <span className="inline-flex items-center gap-1.5"><FileText size={14} className="text-[#d64545]" /> Receipt</span>
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => downloadExcel(`/sales-invoices/${r.id}/export.xlsx`, `GRHAMOY_GST_Invoice_${r.invoiceNo}.xlsx`)}
                  title="Download as Excel"
                >
                  <span className="inline-flex items-center gap-1.5"><FileSpreadsheet size={14} className="text-[#1e8e4f]" /> Excel</span>
                </Button>
                {r.status !== 'Cancelled' && r.status !== 'Paid' && (
                  <Button variant="danger" size="sm" onClick={() => { setOpenWith('cancel'); setOpenId(r.id); }}>
                    Cancel
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <CreateInvoiceModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <InvoiceDetailModal
        open={openId !== null}
        invoiceId={openId}
        onClose={() => setOpenId(null)}
        startWith={openWith}
      />
    </div>
  );
}
